import { useEffect, useState } from 'react'
import { Link, useFetcher, useLocation, useNavigate } from 'react-router'
import { toast } from 'sonner'
import { Icon } from '#app/components/ui/icon'
import {
	Select,
	SelectTrigger,
	SelectValue,
	SelectContent,
	SelectItem,
	SelectSeparator,
} from '#app/components/ui/select'
import { cn } from '#app/utils/misc.tsx'

// Define the type for a single project for clarity
type ProjectInfo = { id: string; name: string }

// Define the type for the fetcher data
type ProjectFetcherData = {
	projects?: ProjectInfo[] // Projects might not be present in action response
	currentProjectId: string | null
	error?: string // Optional error message from action
}

export function ProjectDropdown() {
	const fetcher = useFetcher<ProjectFetcherData>()
	const [open, setOpen] = useState(false)
	// State to hold the list of projects, updated only by the loader
	const [displayedProjects, setDisplayedProjects] = useState<ProjectInfo[]>([])
	const location = useLocation()
	const navigate = useNavigate()

	// Check if we're in a project-specific route
	const isProjectRoute =
		location.pathname.includes('/settings/projects/') &&
		!location.pathname.includes('/settings/projects/list')

	useEffect(() => {
		if (fetcher.state === 'idle' && !fetcher.data) {
			void fetcher.load('/api/resources/project-dropdown')
		}
	}, [fetcher])

	// Update the displayed projects list only when the loader provides it
	useEffect(() => {
		if (fetcher.data?.projects) {
			setDisplayedProjects(fetcher.data.projects)
		}
	}, [fetcher.data?.projects]) // Depend specifically on projects array

	// Get the current project ID from fetcher data (updated by both loader and action)
	const currentProjectId = fetcher.data?.currentProjectId ?? ''

	async function handleProjectChange(projectId: string) {
		// Don't change if it's the same project
		if (projectId === currentProjectId) {
			setOpen(false)
			return
		}

		// Submit the change
		await fetcher.submit(
			{ projectId },
			{ method: 'POST', action: '/api/resources/project-dropdown' },
		)

		setOpen(false) // Close dropdown after submission

		// Navigate to the project dashboard
		const currentPath = location.pathname
		if (currentPath.includes('/project/')) {
			// If we're already in a project route, replace the project ID in the URL
			const newPath = currentPath.replace(
				/\/project\/[^/]+/,
				`/project/${projectId}`,
			)
			void navigate(newPath)
		} else {
			// Otherwise navigate to the project dashboard
			void navigate(`/project/${projectId}/dashboard`)
		}

		toast.success('Project changed')
	}

	return (
		<Select
			value={currentProjectId} // Bind directly to fetcher data
			onValueChange={handleProjectChange}
			disabled={isProjectRoute || fetcher.state !== 'idle'} // Disable during fetch
			open={open}
			onOpenChange={setOpen}
		>
			<SelectTrigger className="flex w-[200px] items-center gap-2 bg-transparent sm:w-[300px]">
				<Icon name="file" className="text-white" />
				<SelectValue placeholder="Select A Project..." />
			</SelectTrigger>
			<SelectContent>
				<Link
					to="/settings/projects/list"
					className={cn(
						'flex w-full cursor-pointer items-center rounded-sm py-1.5 pl-1 text-sm outline-none',
						'hover:bg-accent-foreground hover:text-white',
					)}
					onClick={() => setOpen(false)}
				>
					<Icon name="list-bullet" />
					<span className="pl-2">Manage All Projects</span>
				</Link>
				{/* Use the state variable for the project list */}
				{displayedProjects.length > 0 && <SelectSeparator />}
				{displayedProjects.map((project) => (
					<SelectItem key={project.id} value={project.id}>
						{project.name}
					</SelectItem>
				))}
				{displayedProjects.length > 0 && <SelectSeparator />}
				<Link
					to="/settings/projects/new"
					className={cn(
						'flex w-full cursor-pointer items-center rounded-sm py-1.5 pl-1 text-sm outline-none',
						'hover:bg-accent-foreground hover:text-white',
					)}
					onClick={() => setOpen(false)}
				>
					<Icon name="plus" />
					<span className="pl-2">Add New Project</span>
				</Link>
			</SelectContent>
		</Select>
	)
}
